import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';

import {ChangeLogTimestampCell} from 'src/components/change-log';
import {UserLink} from 'src/components/links/user-link';
import {MainColumnCell, MainColumnHeader} from 'src/components/main-column';

import {Cell, DataTable, TableHeader} from '@pb/kit-component-data-table-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';

import {provisioningTable} from './provisioning-history.module.styl';

import type {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import type {AccountProvisioningHistoryEvent} from 'src/openapi/admin/models/AccountProvisioningHistoryEvent';

const COLUMNS = [
    {columnId: 'email', caption: 'Email', fixed: true, defaultWidth: 280, minWidth: 250, maxWidth: 350},
    {columnId: 'productName', caption: 'Product', defaultWidth: 160, minWidth: 140, maxWidth: 240},
    {columnId: 'status', caption: 'Status', defaultWidth: 120, minWidth: 110, maxWidth: 160},
    {columnId: 'reason', caption: 'Reason', defaultWidth: 200, minWidth: 150},
    {columnId: 'initiator', caption: 'Initiator', defaultWidth: 250, minWidth: 150},
    {columnId: 'timestamp', caption: 'Date and Time', defaultWidth: 170, minWidth: 150, maxWidth: 200},
];
const STATUS_LABELS: Record<string, string> = {ENABLED: 'Enabled', DISABLED: 'Disabled'};
const FLEXIBLE_COLUMNS = new Set(['reason', 'initiator']);

type Props = {
    rows: AccountProvisioningHistoryEvent[],
    page: number,
    pageSize: number,
    getHeaderTopOffset: () => number,
    columnSizing: ReturnType<typeof useDataTableColumnsWidthOnResize>,
    pagination: React.ComponentProps<typeof DataTable>['paginationStripe'],
};

export const ProvisioningHistoryTable = ({
    rows, page, pageSize, getHeaderTopOffset, columnSizing, pagination,
}: Props) => {
    const {getColumnWidthByColumnId, onStopResize} = columnSizing;
    const tableRef = useRef<HTMLDivElement>(null);
    const [tableWidth, setTableWidth] = useState(0);
    useLayoutEffect(() => {
        const table = tableRef.current;
        if (!table) {
            return;
        }
        setTableWidth(table.clientWidth);
        const observer = new ResizeObserver(([entry]) => setTableWidth(entry.contentRect.width));
        observer.observe(table);
        return () => observer.disconnect();
    }, []);
    const columns = useMemo(() => {
        const sized = COLUMNS.map(column => ({
            ...column, fixed: column.fixed ?? false, width: getColumnWidthByColumnId(column.columnId),
        }));
        const flexible = sized.filter(column => FLEXIBLE_COLUMNS.has(column.columnId) && column.width == null);
        const reservedWidth = sized.filter(column => !flexible.includes(column))
            .reduce((total, column) => total + (column.width ?? column.defaultWidth), 0);
        return sized.map(column => tableWidth && flexible.includes(column) ? {
            ...column, defaultWidth: Math.max(column.minWidth, (tableWidth - reservedWidth) / flexible.length),
        } : column);
    }, [getColumnWidthByColumnId, tableWidth]);
    const data = useMemo(() => rows.map(event => ({id: event.logId, columnValues: {...event}})), [rows]);
    const maxRowNumber = (page - 1) * pageSize + rows.length;

    const content = (event: AccountProvisioningHistoryEvent, columnId: string) => {
        switch (columnId) {
            case 'email':
                return event.userId != null ? (
                    <UserLink
                        userId={event.userId}
                        caption={event.email || String(event.userId)}
                        blank={true}
                        place="Provisioning History"
                    />
                ) : event.email || '—';
            case 'initiator':
                return event.initiatorUserId != null ? (
                    <UserLink
                        userId={event.initiatorUserId}
                        caption={event.initiatorUserEmail || String(event.initiatorUserId)}
                        blank={true}
                        place="Provisioning History - Initiator"
                    />
                ) : event.initiatorSystem || event.initiatorUserEmail || '—';
            case 'timestamp':
                return event.timestamp ? <ChangeLogTimestampCell timestamp={event.timestamp}/> : '—';
            case 'status': {
                const status = event.status;
                const label = STATUS_LABELS[status ?? ''] || status || '—';
                return (
                    <EllipsisTextHint title={label}>
                        {label}
                    </EllipsisTextHint>
                );
            }
            default:
                const value = event[columnId as 'productName' | 'reason'];
                return <EllipsisTextHint title={value || '—'}>{value || '—'}</EllipsisTextHint>;
        }
    };

    return (
        <DataTable
            ref={tableRef}
            id="account-provisioning-history-table"
            className={provisioningTable}
            style={{marginRight: 20}}
            data={{rows: data, columns}}
            getHeaderTopOffset={getHeaderTopOffset}
            onStopResize={onStopResize}
            paginationStripe={pagination}
            headers={({column}, options) => (
                <Cell {...options} key={column.columnId} column={column} resizable={true}>
                    {column.columnId === 'email' ? (
                        <MainColumnHeader reserveSelectionSpace={false} maxRowNumber={maxRowNumber}>
                            <strong>Email</strong>
                        </MainColumnHeader>
                    ) : (
                        <TableHeader indent="none" align="top" className="search-header">
                            <strong>{COLUMNS.find(item => item.columnId === column.columnId)?.caption}</strong>
                        </TableHeader>
                    )}
                </Cell>
            )}
            renderRows={({rowIndex, column}, options) => {
                const event = rows[rowIndex];
                const key = `${event.logId}-${column.columnId}`;
                return column.columnId === 'email' ? (
                    <MainColumnCell
                        key={key}
                        reserveSelectionSpace={false}
                        maxRowNumber={maxRowNumber}
                        rowNumber={(page - 1) * pageSize + rowIndex + 1}
                        cellOptions={{...options, column}}>
                        {content(event, column.columnId)}
                    </MainColumnCell>
                ) : (
                    <Cell {...options} key={key} column={column} className="search-result-cell">
                        {content(event, column.columnId)}
                    </Cell>
                );
            }}
        />
    );
};
